import { Box, Divider, Stack } from '@mui/material'
import { intervalToDuration } from 'date-fns'
import { useTranslation } from 'react-i18next'
import useTelemetryStore from 'store/robotTelemetry'
import useMissionStatusStore from 'store/robotMissionStatus'

import RobotControlBoard from './RobotControlBoard'
import RobotDataBlock from './RobotDataBlock'
import RobotImageBlock from './RobotImageBlock'
import RobotLocks from './RobotLocks'
import RobotRouteBlock from './RobotRouteBlock'
import useRobot from 'hooks/useRobot'

const RobotDetails = () => {
  const { t } = useTranslation()
  const telemetry = useTelemetryStore(s => s.telemetry)
  const missionStatus = useMissionStatusStore(s => s.missionStatus)

  const getTextStatus = (percents: number | undefined) => {
    if (!percents) {
      return '-'
    }
    if (percents > 60) {
      return t('robot.good')
    } else if (percents > 30) {
      return t('robot.normal')
    } else {
      return t('robot.low')
    }
  }

  const getDuration = (seconds?: number) => {
    if (!seconds) return '-'
    const duration = intervalToDuration({ start: 0, end: seconds * 1000 })
    return duration.days
      ? `${duration.days}d:${duration.hours?.toString().padStart(2, '0')}:${duration.minutes
          ?.toString()
          .padStart(2, '0')}`
      : `${duration.hours?.toString().padStart(2, '0')}:${duration.minutes?.toString().padStart(2, '0')}`
  }

  const missionCompletion = missionStatus?.mission_execution
    ? Math.round(
        (missionStatus?.mission_execution?.progress /
          missionStatus?.mission_execution?.overall) *
          100
      )
    : '-'

  const speed = telemetry?.velocity !== undefined ? Number(telemetry.velocity.toFixed(1)) : '-'

  const timeLeft = getDuration(missionStatus?.mission_execution?.forecast)

  return (
    <Stack direction="column" justifyContent="space-between" spacing={1} width="100%" height="100%" overflow={'auto'}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        width="100%"
        height={{ sm: '40%' }}
        minHeight={{ sm: '220px' }}
      >
        <Box width={{ xs: '100%', sm: '40%' }}>
          <RobotImageBlock activeMode={telemetry.activeMode} />
        </Box>
        <Box width={{ xs: '100%', sm: '60%' }}>
          <RobotControlBoard />
        </Box>
      </Stack>
      <Divider />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} height={{ sm: '25%' }} minHeight={{ sm: '110px' }}>
        <Box width={{ xs: '100%', sm: '25%' }}>
          <RobotDataBlock header={t('robotView.missionComplection')} value={missionCompletion} tooltip="" />
        </Box>
        <Box width={{ xs: '100%', sm: '25%' }}>
          <RobotDataBlock header={t('robotView.timeLeft')} value={timeLeft} tooltip="" />
        </Box>
        <Box width={{ xs: '100%', sm: '25%' }}>
          <RobotDataBlock header={t('robotView.speed')} value={speed} tooltip="" />
        </Box>
        <Box width={{ xs: '100%', sm: '25%' }}>
          <RobotDataBlock
            header={t('robotView.batteryLevel')}
            value={Math.round(telemetry?.batteryPower || 0)}
            tooltip=""
            // subtitle="time left: 15m 29s"
            onSubtitleClick
          />
        </Box>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} height={{ sm: '25%' }} minHeight={{ sm: '110px' }}>
        <Box width={{ xs: '100%', sm: 'calc(50% + 8px)' }}>
          <RobotRouteBlock
            distance={missionStatus?.task_execution?.overall}
            passed={missionStatus?.task_execution?.progress}
            timeLeft={getDuration(missionStatus?.task_execution?.forecast)}
            possibleCov={missionStatus?.coverage_execution?.forecast}
            cleanedPercent={missionStatus?.coverage_execution?.progress}
          />
        </Box>
        <Box width={{ xs: '100%', sm: '25%' }}>
          <RobotDataBlock
            header={t('robotView.waterTank')}
            value={Math.round(telemetry?.waterTankFilling || 0)}
            tooltip=""
            subtitle={getTextStatus(telemetry?.waterTankFilling)}
          />
        </Box>
        <Box width={{ xs: '100%', sm: '25%' }}>
          <RobotDataBlock
            header={t('robotView.trashTank')}
            value={Math.round(telemetry?.trashFullness || 0)}
            tooltip=""
            subtitle={getTextStatus(telemetry?.trashFullness)}
          />
        </Box>
      </Stack>
      <Divider />
      <Stack direction="row" spacing={1}>
        <Box width="100%">
          <RobotLocks />
        </Box>
      </Stack>
    </Stack>
  )
}

export default RobotDetails
